import { Component, inject, Inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { Permision } from '../../../../core/models/permision.interface';
import { Group, GroupDTO } from '../../../../core/models/group.interface';
import { GroupService } from '../../../../core/services/group/group';
import { ProfileService } from '../../../../core/services/profile/profile';
import { Profile } from '../../../../core/models/users.interface';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AngularDualListBoxModule } from 'angular-dual-listbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { dualListSettings } from '../../../../../main';
import { LoadingService } from '../../../../core/services/loading/loading';
import { ToastrService } from 'ngx-toastr';
import { HttpErrorResponse } from '@angular/common/http';
import { FormUtils } from '../../../../shared/utils';

@Component({
  selector: 'app-group-form',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatCheckboxModule,
    MatIcon,
    AngularDualListBoxModule,
  ],
  templateUrl: './group-form.html',
  styleUrl: './group-form.css',
})
export class GroupForm {
  selected: Permision[] = [];
  permisions: Permision[] = [];
  format = dualListSettings;
  group: Group | null = null;
  private canWrite: boolean = false;
  errorMsg: string[] | null = null;
  private userProfile: Profile | null = null;
  private formBuilder = inject(FormBuilder);

  constructor(
    private groupService: GroupService,
    private dialogRef: MatDialogRef<GroupForm>,
    private profileService: ProfileService,
    private loadingService: LoadingService,
    private toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) data: { group: Group | null },
  ) {
    this.group = data.group;
  }

  ngOnInit(): void {
    this.profileService.asyncErrorValidation$.subscribe((error) => {
      if (error) {
        this.errorMsg = FormUtils.parseErrorMsgs(error);
      }
    });

    this.profileService.userProfile$.subscribe((profile) => {
      if (profile && this.groupService.hasSpecialAccess(profile)) {
        this.canWrite = true;
      }
    });
    this.fetchPermisions();
    if (this.group) {
      this.selected = [...this.group.permisions];
      this.groupForm.patchValue({
        name: this.group.name,
        create: this.group.create,
        delete: this.group.delete,
        update: this.group.update,
        read: this.group.read,
      });
    }
  }

  private fetchPermisions() {
    this.groupService
      .getAllPermisions(this.canWrite)
      .subscribe((permisions) => (this.permisions = permisions));
  }

  closeDialog() {
    this.dialogRef.close();
  }

  groupForm = this.formBuilder.group({
    name: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(20)]],
    create: [false, [Validators.required]],
    delete: [false, [Validators.required]],
    update: [false, [Validators.required]],
    read: [false, [Validators.required]],
  });

  onCreateGroup() {
    if (this.groupForm.status === 'VALID' && !this.group) {
      this.loadingService.show(true);
      const values = this.groupForm.value;
      const payload: GroupDTO = {
        name: this.groupForm.get('name')?.value ?? '',
        read: values.read ? true : false,
        create: values.create ? true : false,
        delete: values.delete ? true : false,
        update: values.update ? true : false,
        permisions: [...this.selected],
      };
      this.groupService.addGroup(payload).subscribe({
        next: () => {
          this.loadingService.show(false);
          const msg = `El grupo fue creado CORRECTAMENTE!`;
          this.toastr.success(msg, 'Exitoso!', {
            progressBar: true,
            timeOut: 5000,
          });
          this.closeDialog();
          this.fetchPermisions();
        },
        error: ({}: HttpErrorResponse) => {
          if (this.errorMsg) {
            this.loadingService.show(false);
            this.toastr.error('Error: ' + this.errorMsg, undefined, {
              progressBar: true,
              timeOut: 5000,
            });
          }
        },
      });
    }

    if (this.groupForm.status === 'VALID' && this.group) {
      this.loadingService.show(true);
      const values = this.groupForm.value;
      const payload: Group = {
        id: this.group.id,
        name: this.groupForm.get('name')?.value ?? '',
        read: values.read ? true : false,
        create: values.create ? true : false,
        delete: values.delete ? true : false,
        update: values.update ? true : false,
        permisions: this.selected,
      };
      this.groupService.editGroup(payload).subscribe({
        next: () => {
          this.loadingService.show(false);
          const msg = `El grupo fue editado CORRECTAMENTE!`;
          this.toastr.success(msg, 'Exitoso!', {
            progressBar: true,
            timeOut: 5000,
          });
          this.closeDialog();
          this.fetchPermisions();
        },
        error: ({}: HttpErrorResponse) => {
          if (this.errorMsg) {
            this.loadingService.show(false);
            this.toastr.error('Error: ' + this.errorMsg, undefined, {
              progressBar: true,
              timeOut: 5000,
            });
          }
        },
      });
    }
  }
}
